/**
 * «Ya pagué»: el aviso que el dueño le manda a Softfi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ ESTO TENÍA QUE EXISTIR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Estaba todo menos la pantalla y el endpoint: la tabla, las reglas, la bandeja
 * del panel de Softfi con sus botones de confirmar y rechazar, y hasta el
 * permiso de facturación marcado como «la única puerta que no se cierra nunca»
 * para que un negocio bloqueado pudiera avisar de que pagó.
 *
 * Sin esto, esa puerta daba a una pared. La bandeja del panel no podía recibir
 * un aviso jamás, y cada renovación era un WhatsApp que había que buscar entre
 * otros treinta y desbloquear a mano.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ Y NO DESBLOQUEA NADA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Se dice con todas las letras en la pantalla. Quien manda el aviso creyendo
 * que con eso ya se abrió la cuenta vuelve a los diez minutos enfadado, y con
 * razón: nadie le avisó.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Send } from "lucide-react";
import { Boton, Campo, Etiquetado, Lista, Nota, Tarjeta, TituloSeccion } from "@/components/ui";
import { mensajeDeError } from "@/lib/api";
// La suscripción de Softfi se cobra en dólares, así que se formatea en dólares
// y NO en la moneda del negocio: un bodeguero que lleva sus precios en bolívares
// leería «Bs 20,00» y creería que pagó otra cosa.
import { formatMoney, MONEDA_POR_DEFECTO } from "@/lib/money";
import { suscripcion } from "@/lib/endpoints";
import { aviso } from "@/stores/ui";

/** Cómo paga la gente en la práctica. Se deja abierto por si escriben otra. */
const METODOS = [
  { valor: "pago_movil", etiqueta: "Pago móvil" },
  { valor: "transferencia", etiqueta: "Transferencia" },
  { valor: "efectivo", etiqueta: "Efectivo" },
  { valor: "zelle", etiqueta: "Zelle" },
  { valor: "binance", etiqueta: "Binance / USDT" },
  { valor: "otro", etiqueta: "Otro" },
];

export function AvisarPago() {
  const qc = useQueryClient();
  const { data: pendiente, isLoading } = useQuery({
    queryKey: ["mi-aviso-de-pago"],
    queryFn: suscripcion.miAviso,
  });

  const [abierto, setAbierto] = useState(false);
  const [monto, setMonto] = useState("");
  const [metodo, setMetodo] = useState("pago_movil");
  const [referencia, setReferencia] = useState("");
  const [nota, setNota] = useState("");
  const [error, setError] = useState<string | null>(null);

  const enviar = useMutation({
    mutationFn: () =>
      suscripcion.avisarPago({
        monto: monto.trim() || "0",
        metodo,
        referencia: referencia.trim(),
        nota: nota.trim() || null,
      }),
    onSuccess: () => {
      setAbierto(false);
      setMonto("");
      setReferencia("");
      setNota("");
      aviso.exito("Pago reportado", "La suscripción se renovará al confirmarse el pago.");
      void qc.invalidateQueries({ queryKey: ["mi-aviso-de-pago"] });
    },
    onError: (e) => setError(mensajeDeError(e)),
  });

  if (isLoading) return null;

  /*
    Ya hay uno en revisión: se enseña el estado, no el formulario.

    ⚠️ Es lo que evita los duplicados. Quien pulsa «avisar» y no ve ningún
    cambio da por hecho que no llegó y lo manda otra vez — y en el panel salen
    cinco filas del mismo pago que hay que resolver una por una.
  */
  if (pendiente) {
    return (
      <Tarjeta className="border-warning/40 bg-warning/5">
        <div className="flex items-start gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-warning/15 text-warning">
            <Clock size={17} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-ink">Pago en revisión</p>
            <p className="mt-1 text-sm text-ink-soft">
              Reportaste un pago de{" "}
              <strong className="tabular">
                {formatMoney(pendiente.monto, MONEDA_POR_DEFECTO)}
              </strong> con la
              referencia <strong className="tabular">{pendiente.referencia}</strong>.
              Al confirmarlo, la suscripción se renovará automáticamente.
            </p>
          </div>
        </div>
      </Tarjeta>
    );
  }

  if (!abierto) {
    return (
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="pulsable flex w-full items-center justify-center gap-2 rounded-[var(--radius-control)] border border-line bg-surface p-3 text-sm font-medium text-ink hover:border-accent/40 hover:bg-surface-hover"
      >
        <Send size={15} /> Reportar un pago
      </button>
    );
  }

  return (
    <Tarjeta className="space-y-3">
      <TituloSeccion
        titulo="Reportar un pago"
        descripcion="Indica el monto y la referencia para verificarlo y renovar la suscripción."
      />

      <Etiquetado label="Monto pagado">
        <Campo
          inputMode="decimal"
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
          placeholder="0,00"
        />
      </Etiquetado>

      <Etiquetado label="Método de pago">
        <Lista value={metodo} onChange={(e) => setMetodo(e.target.value)}>
          {METODOS.map((m) => (
            <option key={m.valor} value={m.valor}>
              {m.etiqueta}
            </option>
          ))}
        </Lista>
      </Etiquetado>

      {/*
        La referencia es lo único obligatorio, y es a propósito: sin ella no se
        puede comprobar contra el banco, y entonces desde el panel no se puede
        hacer nada más que preguntar — que es justo lo que esto viene a evitar.
      */}
      <Etiquetado label="Número de referencia" ayuda="Basta con los últimos dígitos.">
        <Campo
          value={referencia}
          onChange={(e) => setReferencia(e.target.value)}
          placeholder="000111222"
        />
      </Etiquetado>

      <Etiquetado label="Observaciones (opcional)">
        <Campo
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          placeholder="Ej.: el pago se hizo desde otra cuenta"
        />
      </Etiquetado>

      {/*
        ⚠️ Decirlo aquí y no después. Quien manda el aviso creyendo que con eso
        ya se le abrió la cuenta vuelve a los diez minutos enfadado, y tiene
        razón: nadie se lo avisó.
      */}
      <Nota tono="neutro">
        El reporte <strong>no reactiva la cuenta de inmediato</strong>: la suscripción
        se renueva al confirmarse el pago.
      </Nota>

      {error && <Nota tono="peligro">{error}</Nota>}

      <div className="flex gap-2">
        <Boton
          variante="secundario"
          onClick={() => {
            setAbierto(false);
            setError(null);
          }}
          disabled={enviar.isPending}
        >
          Cancelar
        </Boton>
        <Boton
          className="flex-1"
          onClick={() => {
            setError(null);
            enviar.mutate();
          }}
          disabled={referencia.trim().length < 1 || enviar.isPending}
        >
          {enviar.isPending ? "Enviando…" : "Enviar reporte"}
        </Boton>
      </div>
    </Tarjeta>
  );
}
